// hooks/market/useShopProducts.ts
//
// Récupère les produits publiés d'une boutique donnée, pour
// app/(tabs)/market/boutiques/[slug].tsx. Appelle le vrai backend :
//   POST {API_BASE_URL}/market   body: { action: 'get-shop-products', access_token, slug, limit, offset }
// (voir backend/handleMarket.ts → getShopProducts)

import { useState, useCallback, useEffect, useRef } from 'react';
import { API_BASE_URL } from '@/lib/api';
import { getValidToken } from '@/lib/auth';
import type { MarketFeedProduct } from '@/hooks/market/useMarketFeed';

const PAGE_SIZE = 20;

export function useShopProducts(slug: string | undefined) {
  const [products, setProducts] = useState<MarketFeedProduct[]>([]);
  const [loading, setLoading]         = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  const offsetRef  = useRef(0);
  const loadingRef = useRef(false);

  const fetchProducts = useCallback(async (mode: 'initial' | 'more') => {
    if (!slug || loadingRef.current) return;
    loadingRef.current = true;

    if (mode === 'initial') setLoading(true);
    if (mode === 'more')    setLoadingMore(true);
    setError(null);

    try {
      const token = await getValidToken();
      if (!token) {
        setError('session_expired');
        return;
      }

      const currentOffset = mode === 'more' ? offsetRef.current : 0;

      const res = await fetch(`${API_BASE_URL}/market`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action:       'get-shop-products',
          access_token: token,
          slug,
          limit:        PAGE_SIZE,
          offset:       currentOffset,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.error || 'Impossible de charger les produits');
        return;
      }

      const newProducts: MarketFeedProduct[] = data.products || [];

      if (mode === 'more') {
        setProducts(prev => {
          const existingIds = new Set(prev.map(p => p.product_id));
          return [...prev, ...newProducts.filter(p => !existingIds.has(p.product_id))];
        });
      } else {
        setProducts(newProducts);
      }

      offsetRef.current = data.next_offset ?? (currentOffset + newProducts.length);
      setHasMore(Boolean(data.has_more));
    } catch (err) {
      console.error('[useShopProducts]', err);
      setError('network_error');
    } finally {
      setLoading(false);
      setLoadingMore(false);
      loadingRef.current = false;
    }
  }, [slug]);

  useEffect(() => {
    offsetRef.current = 0;
    fetchProducts('initial');
  }, [fetchProducts]);

  const loadMore = useCallback(() => {
    if (!hasMore || loadingRef.current) return;
    fetchProducts('more');
  }, [hasMore, fetchProducts]);

  return { products, loading, loadingMore, error, hasMore, loadMore };
}
